<template>
  <div @click.stop="setCurrentSection" class="document-section" :class="isActive ? 'document-section--active' : ''" :style="{'--depth': depth}">
    <div class="document-section__content">
      <div class="section-actions">
        <button @click.stop="editChild" class="action-btn edit-btn">
          Edit
        </button>
        <button @click.stop="addChild" class="action-btn add-btn">
          +
        </button>
        <button 
          @click.stop="moveUp" 
          class="action-btn move-btn" 
          :disabled="isFirst"
        >
          ↑
        </button>
        <button 
          @click.stop="moveDown" 
          class="action-btn move-btn" 
          :disabled="isLast"
        >
          ↓
        </button>
        <button @click.stop="remove" class="action-btn delete-btn">
          ×
        </button>
      </div>
      <button @click.stop="toggleContent" class="action-btn toggle-btn">
        {{ isContentVisible ? '−' : '+' }}
      </button>
      
      <div class="guidance-container">
        <span v-if="hasGuidance" class="guidance-label">Guidance</span>
      </div>

      <h3 class="document-section__title">
        {{ section.sectionTitle }}
      </h3>
      
      <transition name="slide-fade">
        <div 
          v-if="isContentVisible"
          class="document-section__text"
          v-html="section.content"
        ></div>
      </transition>
    </div>
    
    <div v-if="section.children && section.children.length" class="document-section__children">
      <transition-group name="list">
      <document-section
        v-for="(child, index) in section.children"
        class="document-section__child"
        :key="child.id"
        :section="child"
        :parent-id="section.id"
        :depth="depth + 1"
        :index="index"
        :is-first="index === 0"
        :is-last="index === section.children.length - 1"
        @add-child="$emit('add-child', $event)"
        @move="$emit('move', $event)"
        @remove="$emit('remove', $event)"
      />
      </transition-group>
    </div>

  </div>
</template>

<script>
export default {
  name: "DocumentSection",
  
  props: {
    section: {
      type: Object,
      required: true
    },
    depth: {
      type: Number,
      default: 0
    },
    index: Number,
    isFirst: Boolean,
    isLast: Boolean
  },

  data: () => ({
    active: false,
    isContentVisible: false
  }),

  computed: {
    isActive() {
      return this.section.id === this.$store.state.documentation.currentSectionId;
    },
    hasGuidance() {
      const div = document.createElement("div");
      div.innerHTML = this.section.content;
      return div.querySelector(".guidance") !== null;
    }
  },

  methods: {
    addChild() {
      this.$emit("add-child", this.section.id);
    },

    moveUp() {
      if (!this.isFirst) {
        this.$emit("move", { id: this.section.id, direction: -1 });
      }
    },

    moveDown() {
      if (!this.isLast) {
        this.$emit("move", { id: this.section.id, direction: 1 });
      }
    },

    remove() {
      if (confirm("Delete this section and all its children?")) {
        this.$emit("remove", this.section.id);
      }
    },

    updateTitle(e) {
      this.$emit("update-section", {
        id: this.section.id,
        field: "sectionTitle",
        value: e.target.innerText
      });
    },

    updateContent(e) {
      this.$emit("update-section", {
        id: this.section.id,
        field: "content",
        value: e.target.innerHTML
      });
    },

    editChild() {
      this.$router.push(`/document/section/${this.section.id}`);
      this.$store.state.documentation.currentSection = this.section;
      this.$store.state.documentation.currentSectionId = this.section.id
    },

    setCurrentSection() {
      this.$store.commit("setCurrentSectionId", this.section.id)
    },

    toggleContent() {
      this.isContentVisible = !this.isContentVisible;
    }
  }
};
</script>

<style lang="scss" scoped>
.document-section {
  position: relative;
  transition: all 0.2s;
  cursor: pointer;
  border: 3px transparent solid;
  padding: 4px;

  &--active {
    border: 3px #805ad5 solid;
    border-radius: 8px;
    padding: 4px;
  }

  &__content {
    background-color: white;
    border-radius: 4px;
    border: 1px solid #e0e0e0;
    padding: 20px;
    position: relative;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    
    &:hover {
      .section-actions {
        opacity: 1;
      }
    }

    .toggle-btn {
      position: absolute;
      right: 0px;
      top: 45px !important;
    }
  }

  &__title {
    font-size: 18px;
    font-weight: 400;
    margin-bottom: 12px;
    padding-right: 30px;
    outline: none;
  }

  &__text {
    line-height: 1.6;
    outline: none;
    transition: max-height 0.3s ease-in-out, opacity 0.3s ease-in-out;
    overflow: hidden;
  }
  
  &__children {
    border-left: 2px dashed #e0e0e0;
    padding-left: 30px;
    margin: 8px 0;
  }

  &__child {
    padding: 0px;
    margin: 4px 0;
  }

  .section-actions {
    position: absolute;
    right: 10px;
    top: 10px;
    display: flex;
    gap: 4px;
    opacity: 0;
    transition: opacity 0.2s;
    z-index: 2;
  }

  .action-btn {
    width: 24px;
    height: 24px;
    border: none;
    border-radius: 3px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    padding: 0;
    
    &:disabled {
      opacity: 0.3;
      cursor: not-allowed;
    }

    &.edit-btn {
    width: 24px;
    height: 24px;
    border: none;
    border-radius: 3px;
    padding: 0 18px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    background: #0b6dff;
    color: white;
    }

    &.add-btn {
      background: #4CAF50;
      color: white;
    }

    &.move-btn {
      background: #2196F3;
      color: white;
    }

    &.delete-btn {
      background: #f44336;
      color: white;
    }

    &.toggle-btn {
      position: absolute;
      background: #e2e2e2;
      color: white;
      top: 50%;
      transform: translateY(-50%);
      right: -40px;
    }
  }
}

.guidance-label {
  background-color: #00158A;
  color: white;
  padding: 6px 10px;
  border-radius: 4px;
  font-size: 12px;
}

.guidance-container {
  margin-bottom: 12px;
}

.slide-fade-enter-active, .slide-fade-leave-active {
  transition: max-height 0.3s ease-in-out, opacity 0.3s ease-in-out;
}

.slide-fade-enter, .slide-fade-leave-to {
  max-height: 0;
  opacity: 0;
}

/deep/ .guidance {
  background-color: #00158A !important;
  border-radius: 8px;
  color: #fff;
  padding: 12px;
  margin-bottom: 16px;
}

.guidance p {
  margin: 0 0 8px 0;
}

.guidance em {
  font-style: italic;
}

.guidance ul {
  list-style-type: disc;
  padding-left: 20px;
}

.guidance li {
  margin-bottom: 8px;
}
</style>
